import { Box, Button, Link as ChakraLink, Flex, Image, Text } from "@chakra-ui/react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBars, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import StravaLogo from "../../assets/api_logo_cptblWith_strava_stack_gray.svg";
import { useAuth } from "../../contexts/authContext";
import * as routes from "../../routes";
import { actionButton, navLinkActive, navLinkInactive, navSidebar } from "../../themes/design";

const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "DashboardPage", route: routes.DASHBOARD_PAGE },
    { name: "ActivitiesPage", route: routes.ACTIVITIES_PAGE },
    { name: "SettingsPage", route: routes.SETTINGS_PAGE },
    { name: "ProfilePage", route: routes.PROFILE_PAGE },
  ];

  const handleStravaClick = (): void => {
    // Handle Strava click
  };

  return (
    <Box as="nav" {...navSidebar} p={0} w="100%" maxW="12rem" left={0} top={0} h="100vh">
      <Flex direction="column" align="start" w="100%" h="100%" pb="2" px={4} pt={5}>
        {/* App branding */}
        <Text
          fontFamily="heading"
          fontSize="1.6em"
          color="cyan.300"
          letterSpacing="0.1em"
          fontWeight="700"
          mb={6}
          textTransform="uppercase"
        >
          aNex
        </Text>

        {/* Mobile menu toggle */}
        {isOpen ? null : (
          <Button
            title={t("OpenMenu")}
            onClick={() => setIsOpen(!isOpen)}
            colorScheme="whiteAlpha"
            fontSize="24px"
            leftIcon={<FontAwesomeIcon icon={faBars as IconProp} size="lg" />}
            variant="ghost"
            display={{ base: "block", md: "none" }}
            _hover={{ color: "cyan.300", textDecoration: "none" }}
            alignSelf="flex-start"
          />
        )}

        {/* Navigation Links */}
        <Flex
          direction="column"
          align="start"
          display={isOpen ? "flex" : { base: "none", md: "flex" }}
          w="100%"
          gap={1}
        >
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.route || location.pathname.startsWith(item.route + "/");
            return (
              <ChakraLink
                key={item.route}
                as={NavLink}
                to={item.route}
                onClick={() => setIsOpen(false)}
                {...(isActive ? navLinkActive : navLinkInactive)}
                textDecoration="none"
                fontSize="1em"
                textTransform="uppercase"
                letterSpacing="0.05em"
                py={2}
                px={2}
                borderRadius="md"
                w="100%"
                transition="all 0.2s ease"
                _hover={{
                  ...(isActive ? {} : navLinkInactive._hover),
                  bg: "whiteAlpha.50",
                  textDecoration: "none",
                }}
              >
                {t(item.name)}
              </ChakraLink>
            );
          })}
        </Flex>

        {/* Bottom section: Sign Out + Strava badge */}
        <Flex direction="column" align="start" mt="auto" w="100%" gap={2}>
          <Button
            {...actionButton}
            size="sm"
            w="100%"
            leftIcon={<FontAwesomeIcon icon={faRightFromBracket as IconProp} />}
            onClick={signOut}
          >
            {t("Sign Out")}
          </Button>

          <Flex align="center" w="100%" justify="center">
            <Button
              variant="compatibleWithStrava"
              onClick={handleStravaClick}
              cursor="pointer"
              my={2}
            >
              <Image src={StravaLogo} alt="Strava" w="auto" h="40px" />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Navigation;
